import logo from '../assets/logo_ekynair.png';
import React, { useState } from 'react';
import { BRAND } from '../constants/theme';
import { isGuestSignupRoleString } from '../utils/trusteePermissions';
import { mainLine, tooltipTitle } from '../shared/buildInfo';

const Signup = ({ onSignupSuccess, onSwitchToLogin, darkMode }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'proprietaire'
  });
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('Au moins 8 caractères');
    if (!/[A-Z]/.test(password)) errors.push('Au moins une majuscule');
    if (!/[0-9]/.test(password)) errors.push('Au moins un chiffre');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('Au moins un caractère spécial');
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setPasswordErrors(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      setLoading(false);
      return;
    }

    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      setMessage({ type: 'error', text: errors.join(', ') });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        if (data.needsVerification) {
          setMessage({ type: 'success', text: 'Compte créé ! Vérifiez votre boîte email pour activer votre compte.' });
          // Ne pas rediriger automatiquement — l'utilisateur doit d'abord vérifier son email
        } else {
          setMessage({ type: 'success', text: 'Compte créé avec succès ! Redirection...' });
          setTimeout(() => {
            if (onSignupSuccess) onSignupSuccess();
          }, 1500);
        }
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la création du compte' });
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: darkMode ? '#1E1E32' : '#fff',
    padding: '40px',
    borderRadius: '12px',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e0e0e0',
    boxShadow: darkMode ? 'none' : '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: '550px',
    margin: '0 auto',
    width: '100%'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    border: darkMode ? '1px solid rgba(77, 198, 255, 0.3)' : '1px solid #ddd',
    background: darkMode ? '#2C3E50' : '#fff',
    color: darkMode ? '#E1E1E1' : '#2C3E50',
    fontSize: '14px',
    marginBottom: '16px'
  };

  const buttonStyle = {
    width: '100%',
    padding: '14px',
    background: loading ? '#999' : BRAND.gradient,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    marginTop: '10px'
  };


  return (
    <div style={{ padding: '40px 20px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img src={logo} alt="EkynAir" style={{ height: '80px', marginBottom: '12px' }} />
          <p style={{ color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
            Créer votre compte
          </p>
        </div>

        {message && (
          <div style={{
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            background: message.type === 'success' 
              ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9')
              : (darkMode ? 'rgba(244, 67, 54, 0.2)' : '#ffebee'),
            color: message.type === 'success'
              ? (darkMode ? '#81c784' : '#2e7d32')
              : (darkMode ? '#ef5350' : '#c62828'),
            border: `1px solid ${message.type === 'success' ? '#4caf50' : '#f44336'}`
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
                Prénom
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Jean"
                style={{ ...inputStyle, marginBottom: 0 }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
                Nom
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Dupont"
                style={{ ...inputStyle, marginBottom: 0 }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
              Email <span style={{ color: '#f44336' }}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="votre.email@exemple.com"
              style={inputStyle}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
              Téléphone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="06 12 34 56 78"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '10px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px', fontWeight: '600' }}>
              Quel est votre profil ?
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                {
                  value: 'proprietaire',
                  label: 'Propriétaire',
                  icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                ),
                  desc: 'Je possède un ou plusieurs chevaux et je veux surveiller la qualité de leur air.'
                },
                {
                  value: 'veterinaire',
                  label: 'Vétérinaire',
                  icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/>
                    <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4"/>
                    <circle cx="20" cy="10" r="2"/>
                  </svg>
                ),
                  desc: 'Je suis invité par un propriétaire pour suivre les données de son cheval.'
                },
                {
                  value: 'personne_confiance',
                  label: 'Personne de confiance',
                  icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                ),
                  desc: "Je garde ou surveille le cheval d'un propriétaire qui m'a invité."
                },
              ].map(r => (
                <button key={r.value} type="button" onClick={() => setFormData({ ...formData, role: r.value })}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                    transition: 'all 0.15s', fontFamily: 'inherit', textAlign: 'left',
                    background: formData.role === r.value ? (darkMode ? 'rgba(27,119,241,0.2)' : '#EFF6FF') : (darkMode ? '#2D2D42' : '#F9FAFB'),
                    border: formData.role === r.value ? '2px solid #1B77F1' : `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px', flexShrink: 0 }}>{r.icon}</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700',
                        color: formData.role === r.value ? '#1B77F1' : (darkMode ? '#E1E1E1' : '#1e293b') }}>
                        {r.label}
                      </div>
                      <div style={{ fontSize: '12px', color: darkMode ? '#94A3B8' : '#6b7280', marginTop: '2px', lineHeight: 1.4 }}>
                        {r.desc}
                      </div>
                    </div>
                    {formData.role === r.value && (
                      <div style={{ marginLeft: 'auto', width: '18px', height: '18px', borderRadius: '50%',
                        background: '#1B77F1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'><polyline points='20 6 9 17 4 12'/></svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {isGuestSignupRoleString(formData.role) && (
              <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '8px',
                background: darkMode ? 'rgba(245,158,11,0.1)' : '#FFFBEB',
                border: '1px solid rgba(245,158,11,0.4)', fontSize: '12px', color: '#92400E' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'5px'}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8"/><line x1="12" y1="12" x2="12" y2="16"/></svg>Vous recevrez une invitation par email du propriétaire pour accéder à ses chevaux.
                Si vous n'avez pas encore reçu d'invitation, sélectionnez ce profil et attendez d'être invité.
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
              Mot de passe <span style={{ color: '#f44336' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: '40px' }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: darkMode ? '#E8EDF2' : '#4A5568',
                  fontSize: '12px',
                  fontWeight: '500',
                  textDecoration: 'underline'
                }}
              >
                {showPassword ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            {passwordErrors.length > 0 && formData.password && (
              <div style={{ 
                fontSize: '12px', 
                color: '#f44336', 
                marginTop: '-12px', 
                marginBottom: '12px' 
              }}>
                {passwordErrors.map((err, i) => <div key={i}>• {err}</div>)}
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
              Confirmer le mot de passe <span style={{ color: '#f44336' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: '40px' }}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: darkMode ? '#E8EDF2' : '#4A5568',
                  fontSize: '12px',
                  fontWeight: '500',
                  textDecoration: 'underline'
                }}
              >
                {showConfirmPassword ? 'Masquer' : 'Afficher'}
              </button>
            </div>
          </div>

          <div style={{ 
            background: darkMode ? 'rgba(255,255,255,0.05)' : '#f0f9ff', 
            padding: '12px', 
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '12px',
            color: darkMode ? '#E8EDF2' : '#4A5568'
          }}>
            <strong>Exigences du mot de passe :</strong>
            <div>• Au moins 8 caractères</div>
            <div>• Au moins une majuscule</div>
            <div>• Au moins un chiffre</div>
            <div>• Au moins un caractère spécial (!@#$%...)</div>
          </div>

          <button type="submit" style={buttonStyle} disabled={loading}>
            {loading ? 'Création...' : 'Créer mon compte'}
          </button>
        </form>

        <div style={{ textAlign: 'center', paddingTop: '20px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e0e0e0', marginTop: '20px' }}>
          <span style={{ color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
            Déjà un compte ?
          </span>
          <button
            type="button"
            onClick={onSwitchToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: darkMode ? '#90CAF9' : BRAND.blueText,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '700',
              padding: '4px 8px',
              borderRadius: '4px',
              fontFamily: 'inherit',
              marginLeft: '4px'
            }}
          >
            Se connecter
          </button>
        </div>

        <div title={tooltipTitle} style={{ textAlign: 'center', paddingTop: '20px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '12px' }}>
          {mainLine}
        </div>
      </div>
    </div>
  );
};

export default Signup;
